// Notification store — single source of truth for the bell badge, popover,
// and toast stack. Fed by:
//   - SSE `event: alert` frames (live)
//   - REST hydration on auth (initial unacked list)
// Acknowledgements/resolutions update local state optimistically and are
// reconciled by the next SSE frame for that event id.

import { create } from 'zustand';
import { useShallow } from 'zustand/react/shallow';
import type { AlertEvent, AlertSeverity } from '@/types';

const MAX_KEEP = 50;     // hard cap on retained events in store (popover only shows recent)
const MAX_TOAST = 3;     // max simultaneous toasts on screen

interface PendingToast {
  id:       string;
  eventId:  string;
  severity: AlertSeverity;
  /** Auto-dismiss at this ms timestamp. */
  expires:  number;
}

interface NotificationStore {
  events:        Record<string, AlertEvent>;        // by event id
  /** Insertion order — newest first. */
  order:         string[];
  toasts:        PendingToast[];
  /** Last connection-restored timestamp; used to suppress backfill toasts. */
  hydratedAt:    number | null;

  /** Push from SSE — adds toast if event is new and after hydration. */
  ingest:        (ev: AlertEvent) => void;
  /** Replace state from REST hydration — no toasts. */
  hydrate:       (events: AlertEvent[]) => void;
  /** Optimistic ack — sets local acked_at; server reconciles via SSE. */
  ackLocal:      (id: string, who: string) => void;
  /** Remove a toast by its toast id (not event id). */
  dismissToast:  (id: string) => void;
  /** Remove acked / cleared events older than 60s from popover (keep in DB). */
  trimResolved:  () => void;
}

export function severityFromEvent(ev: AlertEvent): AlertSeverity {
  if (ev.acked_at || ev.resolved_at) return 'cleared';
  const lvl = (ev.stage_level ?? '').toLowerCase();
  if (lvl === 'fault' || lvl === 'error' || lvl === 'critical') return 'fault';
  if (lvl === 'warn' || lvl === 'warning')                       return 'warning';
  return 'info';
}

export const useNotificationStore = create<NotificationStore>((set, get) => ({
  events:     {},
  order:      [],
  toasts:     [],
  hydratedAt: null,

  ingest: (ev) => {
    const wasKnown = !!get().events[ev.id];
    const post = !!get().hydratedAt;
    set((s) => {
      const events = { ...s.events, [ev.id]: ev };
      const order  = wasKnown ? s.order : [ev.id, ...s.order].slice(0, MAX_KEEP);
      let toasts   = s.toasts;
      if (!wasKnown && post) {
        const t: PendingToast = {
          id:       `t_${ev.id}_${Date.now()}`,
          eventId:  ev.id,
          severity: severityFromEvent(ev),
          expires:  Date.now() + 8_000,
        };
        toasts = [t, ...s.toasts].slice(0, MAX_TOAST);
      }
      return { events, order, toasts };
    });
  },

  hydrate: (rows) => {
    const events: Record<string, AlertEvent> = {};
    const order: string[] = [];
    for (const r of [...rows].sort((a, b) => b.fired_at.localeCompare(a.fired_at))) {
      events[r.id] = r;
      order.push(r.id);
      if (order.length >= MAX_KEEP) break;
    }
    set({ events, order, hydratedAt: Date.now() });
  },

  ackLocal: (id, who) => set((s) => {
    const ev = s.events[id];
    if (!ev) return s;
    const now = new Date().toISOString();
    return {
      events: { ...s.events, [id]: { ...ev, acked_at: now, acked_by: who } },
      // remove any associated toasts
      toasts: s.toasts.filter((t) => t.eventId !== id),
    };
  }),

  dismissToast: (id) => set((s) => ({ toasts: s.toasts.filter((t) => t.id !== id) })),

  trimResolved: () => set((s) => {
    const cutoff = Date.now() - 60_000;
    const order  = s.order.filter((id) => {
      const ev = s.events[id];
      if (!ev) return false;
      const ack = ev.acked_at ? new Date(ev.acked_at).getTime() : 0;
      const res = ev.resolved_at ? new Date(ev.resolved_at).getTime() : 0;
      const resolved = Math.max(ack, res);
      return !resolved || resolved > cutoff;
    });
    return { order };
  }),
}));

// ── Selectors / hooks ────────────────────────────────────────────────────────

export function useUnackedCount(): number {
  return useNotificationStore((s) => {
    let n = 0;
    for (const id of s.order) {
      const ev = s.events[id];
      if (ev && !ev.acked_at && !ev.resolved_at) n++;
    }
    return n;
  });
}

export function useFiringNow(): boolean {
  return useNotificationStore((s) => {
    const cutoff = Date.now() - 60_000;
    for (const id of s.order) {
      const ev = s.events[id];
      if (!ev || ev.acked_at || ev.resolved_at) continue;
      if (new Date(ev.fired_at).getTime() > cutoff) return true;
    }
    return false;
  });
}

export function useRecentEvents(limit = 20): AlertEvent[] {
  return useNotificationStore(useShallow((s) => {
    const out: AlertEvent[] = [];
    for (const id of s.order) {
      const ev = s.events[id];
      if (!ev) continue;
      out.push(ev);
      if (out.length >= limit) break;
    }
    return out;
  }));
}

export function useToasts(): PendingToast[] {
  return useNotificationStore(useShallow((s) => s.toasts));
}
